import React, { Component } from 'react';
import { set, omit, isEmpty, debounce } from 'lodash/fp';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Panel, SelectPicker, DateRangePicker, Button } from 'rsuite';
import { viewAccess } from '../../../helpers/permission';
import * as EXPORT_TYPES from '../../../constants/inventory/InventoryExportTypes';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import {
  AddReasonOptions,
  RemoveReasonOptions,
  MoveReasonOption
} from './reason-options';
import ExportButton from '../../../containers/inventory/ExportCsvButton';
import AccessControl from '../../AccessControl';

const ReasonOptions = [
  ...AddReasonOptions,
  ...RemoveReasonOptions,
  MoveReasonOption
];

const StyledSelectPicker = styled(SelectPicker)`
  width: 130px;
  margin-left: 10px;
`;

const ActionWrapper = styled.div`
  top: 10px;
  float: right;
`;

const defaultQueryState = {
  userId: null,
  itemId: null,
  inventoryLocationId: null,
  inventoryLocationZoneId: null,
  tagId: null,
  createdAt: []
};

class InventoryAuditLogSearch extends Component {
  debounceSubmit = debounce(1000)(() => {
    const { query, onSubmit } = this.props;
    onSubmit(query);
  });

  constructor(props) {
    super(props);
    this.state = defaultQueryState;
  }

  static getDerivedStateFromProps({ query }) {
    return { ...defaultQueryState, ...query };
  }

  componentDidMount() {
    const { onInit } = this.props;
    onInit();
  }

  onQueryChange = (key, val) => {
    this.setState({ [key]: val });

    const { onChange, query } = this.props;
    const newQuery = val ? set(key, val, query) : omit(key, query);
    onChange(newQuery);
    this.debounceSubmit();
  };

  handleCreatedAtChange = createdAt => {
    this.onQueryChange('createdAt', createdAt);
  };

  handleUserChange = userId => {
    this.onQueryChange('userId', userId);
  };

  handleReasonChange = category => {
    this.onQueryChange('category', category);
  };

  handleItemChange = itemId => {
    this.onQueryChange('itemId', itemId);
  };

  handleLocationChange = locationId => {
    this.onQueryChange('inventoryLocationId', locationId);
  };

  handleZoneChange = zoneId => {
    this.onQueryChange('inventoryLocationZoneId', zoneId);
  };

  handleTagChange = zoneId => {
    this.onQueryChange('tagId', zoneId);
  };

  handleSubmit = () => {
    const { onSubmit, query } = this.props;
    onSubmit(query);
  };

  handleReset = () => {
    const { onReset, onSubmit } = this.props;
    this.setState(defaultQueryState);
    onReset();
    onSubmit({});
  };

  render() {
    const {
      query,
      userOptions,
      itemOptions,
      locationOptions,
      zoneOptions,
      tagOptions,
      isFetching,
      isItemSpecific
    } = this.props;
    const {
      userId,
      itemId,
      category,
      inventoryLocationId,
      inventoryLocationZoneId,
      tagId,
      createdAt
    } = this.state;
    return (
      <Panel bordered>
        <DateRangePicker
          style={{ width: 200 }}
          value={createdAt}
          placeholder="Date range"
          onChange={this.handleCreatedAtChange}
          disabled={isFetching}
        />
        <StyledSelectPicker
          value={userId}
          data={userOptions}
          appearance="default"
          placeholder="User"
          onChange={this.handleUserChange}
          disabled={isFetching}
        />
        <StyledSelectPicker
          value={category}
          data={ReasonOptions}
          groupBy="group"
          appearance="default"
          placeholder="Reason"
          onChange={this.handleReasonChange}
        />
        {!isItemSpecific && (
          <StyledSelectPicker
            value={itemId}
            data={itemOptions}
            appearance="default"
            placeholder="Item"
            onChange={this.handleItemChange}
            disabled={isFetching}
          />
        )}
        <StyledSelectPicker
          value={inventoryLocationId}
          data={locationOptions}
          appearance="default"
          placeholder="Location"
          onChange={this.handleLocationChange}
          disabled={isFetching}
        />
        <StyledSelectPicker
          value={inventoryLocationZoneId}
          data={zoneOptions}
          appearance="default"
          groupBy="location"
          placeholder="Zone"
          onChange={this.handleZoneChange}
          disabled={isFetching}
        />
        {!isItemSpecific && (
          <StyledSelectPicker
            value={tagId}
            data={tagOptions}
            appearance="default"
            placeholder="Tag"
            onChange={this.handleTagChange}
            disabled={isFetching}
          />
        )}
        <ActionWrapper>
          <AccessControl
            allowOnlyFor={viewAccess(PERMISSIONS.EXPORT_ITEMS_LOGS)}
          >
            {!isFetching && (
              <ExportButton
                style={{ marginRight: 10 }}
                type={EXPORT_TYPES.EXPORT_ITEMS_LOGS}
                searchQuery={omit(['limit', 'order', 'page'], query)}
              />
            )}
          </AccessControl>

          <Button
            disabled={isFetching || isEmpty(query)}
            onClick={this.handleReset}
            appearance="ghost"
          >
            Clear
          </Button>
        </ActionWrapper>
      </Panel>
    );
  }
}

const QueryType = PropTypes.shape({
  userId: PropTypes.number,
  itemId: PropTypes.number,
  inventoryLocationId: PropTypes.number,
  inventoryLocationZoneId: PropTypes.number,
  tagId: PropTypes.number
});

const OptionType = PropTypes.shape({
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired
});

const ZoneOptionType = PropTypes.shape({
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired
});

InventoryAuditLogSearch.propTypes = {
  query: QueryType,
  onInit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isItemSpecific: PropTypes.bool,
  userOptions: PropTypes.arrayOf(OptionType).isRequired,
  itemOptions: PropTypes.arrayOf(OptionType).isRequired,
  locationOptions: PropTypes.arrayOf(OptionType).isRequired,
  zoneOptions: PropTypes.arrayOf(ZoneOptionType).isRequired,
  tagOptions: PropTypes.arrayOf(OptionType).isRequired
};

InventoryAuditLogSearch.defaultProps = {
  query: {},
  isItemSpecific: false
};

export default InventoryAuditLogSearch;
